<template>
  <div class="login-wrapper">
    <div class="login-box">
      <!-- Standard Login Form -->
      <el-form
        v-if="!twoFactorRequired"
        ref="loginForm"
        :model="loginForm"
        :rules="rules"
        @keyup.enter.native="checkForm"
      >
        <div class="logo">
          <img src="../assets/images/logo.svg" width="300" />
        </div>
        <el-form-item label="E-Mail-Adresse" prop="email">
          <el-input id="email" v-model.trim="loginForm.email"></el-input>
        </el-form-item>
        <el-form-item label="Passwort" prop="password">
          <el-input
            id="password"
            v-model="loginForm.password"
            type="password"
            show-password
          ></el-input>
        </el-form-item>
        <el-form-item>
          <el-button
            type="primary"
            class="login-button"
            :loading="loading"
            @click="checkForm"
          >
            Einloggen
          </el-button>
        </el-form-item>
        <div class="links-container">
          <el-link @click="forgotPassword">Passwort vergessen?</el-link>
        </div>
      </el-form>

      <!-- Two-Factor Verification Form -->
      <div v-else class="two-factor-form">
        <div class="logo">
          <img src="../assets/images/logo.svg" width="300" />
        </div>
        <h3 class="two-factor-title">Zwei-Faktor-Authentifizierung</h3>
        <p class="two-factor-description">
          Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein. Sie
          können auch einen Backup-Code verwenden.
        </p>
        <el-input
          ref="otpInput"
          v-model="otpCode"
          placeholder="Code eingeben"
          maxlength="20"
          class="otp-input"
          @keyup.enter.native="verifyTwoFactor"
        ></el-input>
        <el-checkbox v-model="rememberDevice" class="remember-device">
          Dieses Gerät merken (30 Tage)
        </el-checkbox>
        <el-button
          type="primary"
          class="login-button"
          :loading="verifying"
          :disabled="!otpCode"
          @click="verifyTwoFactor"
        >
          Verifizieren
        </el-button>
        <div class="links-container">
          <el-link :disabled="verifying" @click="cancelTwoFactor">
            Zurück zum Login
          </el-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as log from "loglevel"
import { mapGetters } from "vuex"
import FormMixin from "@/mixins/form.js"
import ApiErrorHandlerMixin from "@/mixins/api_error_handler.js"
import UserRepository from "@/repositories/user_repository.js"
import TwoFactorRepository from "@/repositories/two_factor_repository.js"
import utils from "@/utils"

export default {
  metaInfo: {
    title: "Login"
  },
  mixins: [FormMixin, ApiErrorHandlerMixin],
  data() {
    return {
      loginForm: {
        email: "",
        password: ""
      },
      rules: {
        email: [
          {
            type: "email",
            required: true,
            message: "Bitte gültige E-Mail-Adresse angeben",
            trigger: "blur"
          }
        ],
        password: [
          { required: true, message: "Bitte Passwort angeben", trigger: "blur" }
        ]
      },
      loading: false,
      // Two-factor authentication state
      twoFactorRequired: false,
      twoFactorToken: null,
      otpCode: "",
      rememberDevice: false,
      verifying: false
    }
  },
  computed: {
    ...mapGetters("auth", ["userData"])
  },
  methods: {
    forgotPassword() {
      this.$router.push({ name: "ForgotPasswordPage" })
    },
    checkForm() {
      this.$refs["loginForm"].validate(valid => {
        if (valid) {
          this.submitForm()
        } else {
          this.showFormErrors()
        }
      })
    },
    async submitForm() {
      this.loading = true
      try {
        let data = await UserRepository.loginUser(
          this.loginForm.email,
          this.loginForm.password
        )
        this.loading = false

        // Check if 2FA is required
        if (data.two_factor_required && data.two_factor_token) {
          this.twoFactorRequired = true
          this.twoFactorToken = data.two_factor_token
          // Focus the OTP input after the view updates
          this.$nextTick(() => {
            if (this.$refs.otpInput) {
              this.$refs.otpInput.focus()
            }
          })
          return
        }

        // No 2FA required, proceed with login
        this.completeLogin(data)
      } catch (error) {
        this.loading = false
        if (error.response && error.response.status === 422) {
          this.$message({
            showClose: true,
            message: "E-Mail-Adresse und Passwort stimmen nicht überein.",
            type: "error",
            duration: 10000
          })
        } else {
          this.handleApiError(error)
        }
      }
    },

    async verifyTwoFactor() {
      if (!this.otpCode || this.verifying) return

      this.verifying = true
      const pendingToken = this.twoFactorToken
      try {
        const deviceFingerprint = this.generateDeviceFingerprint()
        const deviceName = this.getDeviceName()

        const data = await TwoFactorRepository.verify(
          this.twoFactorToken,
          this.otpCode,
          this.rememberDevice,
          deviceFingerprint,
          deviceName
        )

        if (!this.twoFactorRequired || this.twoFactorToken !== pendingToken) {
          return
        }

        // Store device token if returned (wrapped in try-catch to handle
        // Safari private browsing or quota exceeded errors gracefully)
        if (data.device_token) {
          try {
            localStorage.setItem("familien-app-device-token", data.device_token)
          } catch (storageError) {
            // Silently ignore storage errors - device token is optional
            // and login should still complete successfully
          }
        }

        this.completeLogin(data)
      } catch (error) {
        if (error.response && error.response.status === 422) {
          const errorData = error.response.data
          const message =
            errorData.errors && errorData.errors[0]
              ? errorData.errors[0]
              : "Ungültiger Code. Bitte versuchen Sie es erneut."
          this.$message({
            showClose: true,
            message: message,
            type: "error",
            duration: 10000
          })
        } else {
          this.handleApiError(error)
        }
      } finally {
        this.verifying = false
      }
    },

    cancelTwoFactor() {
      this.twoFactorRequired = false
      this.twoFactorToken = null
      this.otpCode = ""
      this.rememberDevice = false
    },

    completeLogin(data) {
      this.$store.commit("auth/login", data)

      utils.addErrorBreadcrumb({
        category: "Auth",
        action: "Logged in",
        metadata: {
          userType: this.$store.getters["auth/userType"],
          userId: this.userData.id
        }
      })

      if (this.$route.query.sso && this.$route.query.sig) {
        this.$router.push({
          name: "DiscourseSSOPage",
          query: { sso: this.$route.query.sso, sig: this.$route.query.sig }
        })
        return
      }

      if (
        this.$store.getters["auth/isSuperAdmin"] ||
        this.$store.getters["auth/isRegionUser"]
      ) {
        this.$router
          .push({
            name: "DashboardPage",
            query: { allow_redirect: true }
          })
          .catch(() => {
            log.info("TEMP: Ignoring route redirect error after login")
          })
      } else if (this.$store.getters["auth/isOrganizationUser"]) {
        this.$router.push({ name: "EventsPage" })
      } else {
        this.$router.push({
          name: "SignupPage",
          params: { id: this.userData.region_id }
        })
      }
    },

    generateDeviceFingerprint() {
      // Simple device fingerprint based on browser info
      const components = [
        navigator.userAgent,
        navigator.language,
        screen.width + "x" + screen.height,
        new Date().getTimezoneOffset()
      ]
      return btoa(components.join("|")).substring(0, 32)
    },

    getDeviceName() {
      const ua = navigator.userAgent
      // Check Edge first - modern Edge UA contains "Edg/" and also "Chrome"
      if (ua.includes("Edg")) return "Edge Browser"
      if (ua.includes("Chrome")) return "Chrome Browser"
      if (ua.includes("Firefox")) return "Firefox Browser"
      if (ua.includes("Safari")) return "Safari Browser"
      return "Web Browser"
    }
  }
}
</script>

<style lang="scss" scoped>
.login-wrapper {
  display: flex;
  height: 100vh;
  width: 100vw;
  align-items: center;
  justify-content: center;
}

.login-box {
  width: 400px;
  box-shadow: 0 0 25px #cac6c6;
  border-radius: 5px;
  padding: 25px;
}

.login-button {
  width: 100%;
}

.links-container {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.logo {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

// Two-factor authentication styles
.two-factor-form {
  text-align: center;
}

.two-factor-title {
  margin: 16px 0 8px;
  font-size: 18px;
  font-weight: 600;
}

.two-factor-description {
  margin-bottom: 20px;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.otp-input {
  margin-bottom: 16px;

  ::v-deep input {
    text-align: center;
    font-size: 18px;
    letter-spacing: 4px;
  }
}

.remember-device {
  display: block;
  margin-bottom: 22px;
}
</style>
